import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { brandName } from '../lib/brand';
import { Download, Share, PlusSquare, X } from 'lucide-react';

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

export function InstallButton({ big = false, card = false }) {
  const [evt, setEvt] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    const done = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', h);
    window.addEventListener('appinstalled', done);
    return () => { window.removeEventListener('beforeinstallprompt', h); window.removeEventListener('appinstalled', done); };
  }, []);

  if (installed) return big ? <span data-testid="install-done" className="rounded-full border border-emerald-500/40 px-4 py-2.5 text-xs text-emerald-300">✓ App installata</span> : null;
  if (!big && !card && !evt && !isIOS()) return null;
  const click = async () => {
    if (evt) { evt.prompt(); const r = await evt.userChoice; if (r.outcome === 'accepted') setInstalled(true); setEvt(null); }
    else setSheet(true);
  };
  if (card) return (
    <>
      <button data-testid="install-card-btn" onClick={click} className="flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-semibold border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 transition-colors"><Download className="h-3.5 w-3.5" />Installa</button>
      {sheet && createPortal(<IOSSheet ios={isIOS()} onClose={() => setSheet(false)} />, document.body)}
    </>
  );
  return (
    <>
      <button data-testid={big ? 'install-pwa-hero-btn' : 'install-pwa-btn'} onClick={click}
        className={`neon-pulse flex items-center gap-2 rounded-full font-bold tracking-wider text-white bg-gradient-to-r from-violet-600 to-cyan-600 hover:brightness-110 transition-[filter] ${big ? 'px-6 py-3 text-sm bg-none bg-titan-surface/80 border border-cyan-400/50' : 'px-3.5 py-1.5 text-xs'}`}>
        <Download className={big ? 'h-4 w-4' : 'h-3.5 w-3.5'} /> {big ? 'INSTALLA SULLA HOME' : 'INSTALLA'}
      </button>
      {sheet && createPortal(<IOSSheet ios={isIOS()} onClose={() => setSheet(false)} />, document.body)}
    </>
  );
}

function IOSSheet({ onClose, ios }) {
  const name = brandName();
  const steps = ios ? [
    { icon: Share, t: 'Tocca il pulsante Condividi', d: 'nella barra in basso di Safari' },
    { icon: PlusSquare, t: 'Scegli "Aggiungi a Home"', d: 'scorri il menu se non lo vedi' },
    { icon: Download, t: 'Conferma con "Aggiungi"', d: `${name} si apre a schermo intero, anche offline` },
  ] : [
    { icon: Share, t: 'Apri il menu del browser', d: 'i tre puntini ⋮ in alto a destra (Chrome / Edge)' },
    { icon: PlusSquare, t: 'Scegli "Installa app" o "Aggiungi a schermata Home"', d: 'su PC trovi anche l\'icona nella barra indirizzi' },
    { icon: Download, t: 'Conferma "Installa"', d: `${name} si apre come app, anche offline` },
  ];
  return (
    <div data-testid="ios-install-sheet" className="fixed inset-0 z-[100] flex items-end bg-black/60" onClick={onClose}>
      <div className="w-full rounded-t-3xl bg-titan-surface border-t border-violet-500/30 p-6 pb-10 pb-safe rise" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-slate-600" />
        <div className="flex justify-between items-center mb-5">
          <div className="text-base font-bold text-slate-100">Installa {name} {ios ? 'su iPhone' : 'sulla Home'}</div>
          <button data-testid="ios-sheet-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-4 py-3 rise" style={{ animationDelay: `${i * 0.15}s` }}>
            <div className="h-11 w-11 rounded-xl bg-violet-500/15 border border-violet-400/30 flex items-center justify-center">
              <s.icon className={`h-5 w-5 text-cyan-300 ${i === 0 ? 'animate-bounce' : ''}`} />
            </div>
            <div className="t-chat"><div className="text-sm text-slate-100">{i + 1}. {s.t}</div><div className="text-xs text-slate-400">{s.d}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}
